/**
 * ProcessSteps 组件
 * 以 ProcessStep 为中心的处理步骤展示组件
 * 
 * 设计理念：
 * 1. 默认展开，实时显示AI处理过程
 * 2. 先用JSON格式展示所有ProcessStep，便于调试和开发
 * 3. 每个Step清晰区分，提供良好的视觉层次
 * 4. 支持折叠/展开控制，但不强制折叠
 */
import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';
import { ProcessStep, isFetchMemStep, isUpdateMemStep } from '../../../../types';
import { useTranslation } from 'react-i18next';
import { DebugMode, TextStep, FetchMemResult, UpdateMemResult, ToolError } from './processStepsComps/index';

interface ProcessStepsProps {
  processLog: ProcessStep[];
  isExpanded: boolean;
  onToggle: () => void;
  showToggleButton?: boolean; // 是否显示折叠/展开按钮
  forceTheme?: 'light' | 'dark'; // 强制指定主题，用于预览
}

export const ProcessSteps: React.FC<ProcessStepsProps> = ({
  processLog,
  isExpanded,
  onToggle,
  showToggleButton = true,
  forceTheme
}) => {
  const { t } = useTranslation(['v3/chat', 'common']);


  // 渲染 ProcessStep 内容
  const renderStepContent = (step: ProcessStep, index: number) => {
    switch (step.type) {
      case 'text':
        return <TextStep step={step} forceTheme={forceTheme} />;

      case 'tool_result':
        // 根据工具名称选择特定的渲染组件
        if (isFetchMemStep(step)) {
          return <FetchMemResult step={step} />;
        } else if (isUpdateMemStep(step)) {
          return <UpdateMemResult step={step} />;
        }
        // 其他工具结果使用 DebugMode
        return <DebugMode step={step} stepIndex={index + 1} />;

      case 'tool_error':
        return <ToolError step={step} />;

      default:
        return <DebugMode step={step} stepIndex={index + 1} />;
    }
  };

  // 如果没有数据，不显示组件
  if (!processLog || processLog.length === 0) {
    return null;
  }

  return (
    <div className="w-full">
      {/* 折叠/展开控制按钮 */}
      {showToggleButton && (
        <button
          onClick={onToggle}
          className="flex items-center text-xs text-ios-gray dark:text-ios-dark-gray hover:text-ios-blue dark:hover:text-ios-dark-blue transition-colors mb-2"
        >
          {isExpanded ? (
            <><FiChevronUp className="mr-1" />{t('v3/chat:processSteps.hideThinking')}</>
          ) : (
            <><FiChevronDown className="mr-1" />{t('v3/chat:processSteps.showThinking')}</>
          )}
        </button>
      )}

      {/* ProcessSteps 内容区域 */}
      <AnimatePresence>
        {(isExpanded || !showToggleButton) && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="space-y-3">
              {/* ProcessStep 列表 */}
              {processLog
                .sort((a, b) => (a.sequence || 0) - (b.sequence || 0)) // 按sequence排序
                .map((step, index) => (
                  <motion.div
                    key={`step-${step.sequence || index}-${step.type}`}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2, delay: index * 0.05 }}
                    className="mb-3"
                  >
                    {/* Step 内容 - 使用专门的组件渲染 */}
                    {renderStepContent(step, index)}
                  </motion.div>
                ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ProcessSteps;
